import {Badge, Box, Code, NavLink, rem, ScrollArea, Tooltip} from '@mantine/core';
import {
    IconCalculator,
    IconChartLine,
    IconEdit,
    IconGauge,
    IconHistory,
    IconHome,
    IconReportMoney,
    IconSearch,
    IconUser,
} from '@tabler/icons-react';
import {AccountService} from '@bobex/react-core/services/AccountService';
import {AuthoritiesConstants} from '@bobex/react-core/domain/AuthoritiesModel/constants/AuthoritiesConstants';
import React, {useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {RecentModelAPI} from '@bobex/react-core/domain/RecentModel/RecentModelAPI';
import {RecentAbstractView} from '@bobex/react-core/domain/RecentModel/view/RecentAbstractView';
import {RecentMemberView} from '@bobex/react-core/domain/RecentModel/view/RecentMemberView';
import {RecentAbstractUtil} from '@bobex/react-core/domain/RecentModel/util/RecentAbstractUtil';
import {UserGroupColorUtils} from '@bobex/react-core/utils/UserGroupColorUtils';
import {useLocation, useNavigate} from 'react-router-dom';
import {useSidebarUpdate} from './SidebarProvider';
import {RouteUtils} from '@bobex/react-core/routes/RouteUtils';
import classes from './Sidebar.module.css';

export default function Sidebar({ height = 'calc(100vh - 60px)' }: { height?: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [counterMap] = React.useState<Record<string, number>>({});
  const [history, setHistory] = React.useState<RecentAbstractView[]>([]);
  const [openedGroups, setOpenedGroups] = React.useState<Record<string, boolean>>({});
  const sidebarUpdateContext = useSidebarUpdate();

  useEffect(() => {
    // Initial fetch
    getCounters().then();
    getHistory().then();

    // Set up interval to refresh every minute
    const intervalId = setInterval(() => {
      getCounters().then();
    }, 60000); // 60 000 milliseconds = 1 minute

    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    getHistory().then();
  }, [sidebarUpdateContext.triggerHistoryRefresh]);

  const getCounters = async () => {
    // try {
    //     const response = await CounterApi.getSidebarCounter();
    //     response["assigned-sales.total"] = await AssignationSalesModelAPI.getMyProspectsCounter();
    //     setCounterMap(response);
    //
    // } catch (error) {
    //     console.error(error);
    // }
  };

  const getHistory = async () => {
    RecentModelAPI.getHistory()
      .then((response) => {
        setHistory(response);
      })
      .catch((error) => {
        console.error('Error fetching recent history', error);
      });
  };

  const handleNavigation = (link: string, target?: string) => {
    if (target === '_blank') {
      window.open(link, '_blank');
    } else if (link.startsWith('http')) {
      window.location.href = link;
    } else if (link.includes('/control/')) {
      // Servlet link
      window.location.href = link;
    } else {
      // React route
      navigate(link);
    }
  };

  const isLinkActive = (link: string) => {
    if (link.startsWith('http')) return false;
    if (link.includes('/control/')) return false;
    return location.pathname === link;
  };

  const toggleGroup = (label: string) => {
    setOpenedGroups((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  const getCounter = (counters?: string[]) => {
    if (!counters) return 0;
    return counters.reduce((sum, key) => sum + (counterMap[key] || 0), 0);
  };

  return (
    <ScrollArea scrollbars="y" h={height} offsetScrollbars="y">
      <nav className={classes.sidebarNav}>
        {/* Home */}
        <NavLink
          href={RouteUtils.getBasename() + '/sitemap'}
          label="Home"
          leftSection={<IconHome />}
          childrenOffset={28}
          opened={!!openedGroups['Home']}
          onClick={() => handleNavigation(RouteUtils.getBasename() + '/sitemap')}
        />

        {/* Sales */}
        <NavLink
          label="Sales"
          leftSection={<IconReportMoney />}
          childrenOffset={28}
          opened={!!openedGroups['Sales']}
          onChange={() => toggleGroup('Sales')}
        >
          <NavLink
            href={
              RouteUtils.getBackofficeServletBasename() +
              '/my_assignation_sales?vendor=' +
              AccountService.getId() +
              '&assignationStatus=10&assignationStatus=20&assignationStatus=5'
            }
            label={t('My prospects')}
            leftSection={<IconSearch />}
            rightSection={
              getCounter(['assigned-sales.total']) > 0 ? (
                <Badge size="sm" variant="filled" color="blue" circle>
                  {getCounter(['assigned-sales.total'])}
                </Badge>
              ) : null
            }
            onClick={() =>
              handleNavigation(
                RouteUtils.getBackofficeServletBasename() +
                  '/my_assignation_sales?vendor=' +
                  AccountService.getId() +
                  '&assignationStatus=10&assignationStatus=20&assignationStatus=5'
              )
            }
          />
          <NavLink
            href={RouteUtils.getBackofficeServletBasename() + '/assignation_sales_searchform'}
            label={t('search.prospects')}
            leftSection={<IconSearch />}
            onClick={() =>
              handleNavigation(
                RouteUtils.getBackofficeServletBasename() + '/assignation_sales_searchform'
              )
            }
          />
        </NavLink>

        {/* Users */}
        <NavLink
          href={RouteUtils.getBasename() + '/search-user'}
          label="Users"
          leftSection={<IconUser />}
          childrenOffset={28}
          opened={!!openedGroups['Users']}
          onChange={() => toggleGroup('Users')}
          onClick={() => handleNavigation(RouteUtils.getBasename() + '/search-user')}
        />

        {/* Leads */}
        <NavLink
          href={RouteUtils.getBackofficeServletBasename() + '/bo_partner_rfp_searchform'}
          label={t('Leads')}
          hiddenFrom="lg"
          leftSection={<IconEdit />}
          onClick={() =>
            handleNavigation(RouteUtils.getBackofficeServletBasename() + '/bo_partner_rfp_searchform')
          }
        />

        {/* History */}
        <NavLink
          label={t('History')}
          leftSection={<IconHistory />}
          childrenOffset={28}
          opened={!!openedGroups['History']}
          onChange={() => toggleGroup('History')}
        >
          {history.map((item: RecentAbstractView, index: number) => {
            const LinkIcon = item.type === 2 ? IconUser : IconEdit;
            const iconColor =
              item instanceof RecentMemberView
                ? UserGroupColorUtils.getUserGroupColor(item.memberUserGroupId)
                : '';
            let iconTooltip =
              item instanceof RecentMemberView
                ? UserGroupColorUtils.getUserGroupLabel(item.memberUserGroupId)
                : '';
            if (iconTooltip === 'Buyer') iconTooltip = 'Client';

            return (
              <NavLink
                href={
                  RouteUtils.getBasename() + RecentAbstractUtil.getRecentItemNavigationLink(item)
                }
                key={`history-${index}`}
                label={RecentAbstractUtil.getRecentItemDisplayLabel(item)}
                leftSection={
                  iconTooltip ? (
                    <Tooltip label={iconTooltip} position="right" withArrow>
                      <LinkIcon color={iconColor} />
                    </Tooltip>
                  ) : (
                    <LinkIcon color={iconColor} />
                  )
                }
                active={isLinkActive(RecentAbstractUtil.getRecentItemNavigationLink(item))}
                onClick={(event) => {
                  event.preventDefault();
                  handleNavigation(RecentAbstractUtil.getRecentItemNavigationLink(item));
                }}
              />
            );
          })}
        </NavLink>

        {/* Invoices */}
        <NavLink
          href={RouteUtils.getBasename() + '/search-subscription'}
          label="Invoices"
          leftSection={<IconCalculator />}
          childrenOffset={28}
          opened={!!openedGroups['Invoices']}
          onChange={() => toggleGroup('Invoices')}
          onClick={() => handleNavigation(RouteUtils.getBasename() + '/search-subscription')}
        />

        {/* Search */}
        <NavLink
          label={t('Search')}
          leftSection={<IconSearch />}
          childrenOffset={28}
          opened={!!openedGroups['Search']}
          onChange={() => toggleGroup('Search')}
        >
          <NavLink
            href={RouteUtils.getBasename() + '/joue-search'}
            label="JOUE"
            onClick={() => handleNavigation(RouteUtils.getBasename() + '/joue-search')}
          />
          <NavLink
            href={RouteUtils.getBasename() + '/bda-search'}
            label="BDA"
            onClick={() => handleNavigation(RouteUtils.getBasename() + '/bda-search')}
          />
          <NavLink
            href={RouteUtils.getBasename() + '/bda-error-search?languageId=5'}
            label="BDA errors fr"
            rightSection={
              getCounter(['bda-errors-fr']) > 0 ? (
                <Badge size="sm" variant="filled" color="blue" circle>
                  {getCounter(['bda-errors-fr'])}
                </Badge>
              ) : null
            }
            onClick={() =>
              handleNavigation(RouteUtils.getBasename() + '/bda-error-search?languageId=5')
            }
          />
          <NavLink
            href={RouteUtils.getBasename() + '/bda-error-search?languageId=2'}
            label="BDA errors nl"
            rightSection={
              getCounter(['bda-errors-nl']) > 0 ? (
                <Badge size="sm" variant="filled" color="blue" circle>
                  {getCounter(['bda-errors-nl'])}
                </Badge>
              ) : null
            }
            onClick={() =>
              handleNavigation(RouteUtils.getBasename() + '/bda-error-search?languageId=2')
            }
          />
        </NavLink>

        {/* Dashboard */}
        <NavLink
          label={t('Dashboard')}
          leftSection={<IconGauge />}
          childrenOffset={28}
          opened={!!openedGroups['Dashboard']}
          onChange={() => toggleGroup('Dashboard')}
        >
          <NavLink
            href={RouteUtils.getBackofficeServletBasename() + '/bo_dashboard_inc_govex'}
            label={t('Dashboard Incentives')}
            leftSection={<IconGauge />}
            onClick={() =>
              handleNavigation(
                RouteUtils.getBackofficeServletBasename() + '/bo_dashboard_inc_govex'
              )
            }
          />
          <NavLink
            href={
              RouteUtils.getBackofficeServletBasename() +
              '/bo_dashboard_sales_work?userid=' +
              AccountService.getId()
            }
            label={t('Working dashboard')}
            leftSection={<IconGauge />}
            onClick={() =>
              handleNavigation(
                RouteUtils.getBackofficeServletBasename() +
                  '/bo_dashboard_sales_work?userid=' +
                  AccountService.getId()
              )
            }
          />
          <NavLink
            href={
              RouteUtils.getBackofficeServletBasename() +
              '/gsupplier_marketing?vendor=' +
              AccountService.getId() +
              '&startdate=20241201&enddate=20251217'
            }
            label={t('Dashboard prospects')}
            leftSection={<IconGauge />}
            onClick={() =>
              handleNavigation(
                RouteUtils.getBackofficeServletBasename() +
                  '/gsupplier_marketing?vendor=' +
                  AccountService.getId() +
                  '&startdate=20241201&enddate=20251217'
              )
            }
          />
          <NavLink
            href={
              RouteUtils.getBackofficeServletBasename() +
              '/bo_dashboard_sales_opportunities?userid=' +
              AccountService.getId()
            }
            label={t('Menu.opportunities_pipeline')}
            leftSection={<IconGauge />}
            onClick={() =>
              handleNavigation(
                RouteUtils.getBackofficeServletBasename() +
                  '/bo_dashboard_sales_opportunities?userid=' +
                  AccountService.getId()
              )
            }
          />
          <NavLink
            href={
              RouteUtils.getBackofficeServletBasename() +
              '/bo_dashboard_sales?userid=' +
              AccountService.getId()
            }
            label={t('Menu.customer_dashboard')}
            leftSection={<IconGauge />}
            onClick={() =>
              handleNavigation(
                RouteUtils.getBackofficeServletBasename() +
                  '/bo_dashboard_sales?userid=' +
                  AccountService.getId()
              )
            }
          />
          {AccountService.hasAuthority([AuthoritiesConstants.VIEW_CHARTS_ADMIN]) && (
            <NavLink
              href={RouteUtils.getBasename() + '/payments-dashboard'}
              label={t('payments.title')}
              leftSection={<IconGauge />}
              onClick={() => handleNavigation(RouteUtils.getBasename() + '/payments-dashboard')}
            />
          )}
          {AccountService.hasAuthority(['READ_LAWYER_DASHBOARD']) && (
            <NavLink
              href={RouteUtils.getBasename() + '/lawyer-dashboard'}
              label={t('lawyerDashboard.title')}
              leftSection={<IconGauge />}
              onClick={() => handleNavigation(RouteUtils.getBasename() + '/lawyer-dashboard')}
            />
          )}
        </NavLink>

        {/* Charts */}
        <NavLink
          href={RouteUtils.getBackofficeServletBasename() + '/charts_home'}
          label={t('Charts')}
          childrenOffset={28}
          opened={!!openedGroups['Charts']}
          onChange={() => toggleGroup('Charts')}
          leftSection={<IconChartLine />}
          onClick={() =>
            handleNavigation(RouteUtils.getBackofficeServletBasename() + '/charts_home')
          }
        />

        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: rem(20),
            marginBottom: rem(10),
            gap: rem(6)
          }}
        >
          <Code style={{ fontSize: rem(10) }}>v1.0.0</Code>
          <Code style={{ fontSize: rem(10) }}>{AccountService.getUser()?.hostname || window.location.hostname}</Code>
        </Box>
      </nav>
    </ScrollArea>
  );
}
